import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { Check, Eraser, FileSignature, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { formatBRL } from '@/lib/format';

const TERMS = [
  'O CONTRATANTE declara estar ciente e de acordo com os serviços de turismo descritos, incluindo roteiro, datas, hospedagem e transporte.',
  'O pagamento deverá ser realizado conforme as condições acordadas. O não pagamento pode acarretar cancelamento da reserva.',
  'A política de cancelamento e remarcação segue as regras do fornecedor responsável pela viagem e a legislação vigente.',
  'O CONTRATANTE é responsável pela documentação pessoal, vacinas e exigências do destino. As informações de saúde fornecidas servem para atendimento em caso de emergência.',
  'A AGÊNCIA atua como intermediária entre o CONTRATANTE e os fornecedores de serviços turísticos.',
];

export default function SignContractDialog({ open, onOpenChange, sale, onSigned }) {
  const sigRef = useRef(null);
  const [step, setStep] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [terms, setTerms] = useState(TERMS);
  const [cancelIntro, setCancelIntro] = useState('');
  const [cancelPolicy, setCancelPolicy] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get('/settings')
      .then((r) => {
        if (r.data && Array.isArray(r.data.contract_terms) && r.data.contract_terms.length) setTerms(r.data.contract_terms);
        if (r.data && typeof r.data.cancellation_intro === 'string') setCancelIntro(r.data.cancellation_intro);
        if (r.data && Array.isArray(r.data.cancellation_policy)) setCancelPolicy(r.data.cancellation_policy);
      })
      .catch(() => {});
  }, [open]);

  const reset = () => { setStep(1); setAccepted(false); };
  const handleOpenChange = (o) => { if (!o) reset(); onOpenChange(o); };
  const clearSig = () => sigRef.current && sigRef.current.clear();

  const submit = async () => {
    if (!accepted) { toast.error('É necessário aceitar os termos'); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error('Por favor, assine no quadro abaixo'); return; }
    setSubmitting(true);
    try {
      const signature = sigRef.current.toDataURL('image/png');
      const r = await api.post(`/portal/contracts/${sale.sale_id}/sign`, { accepted_terms: true, signature_b64: signature });
      toast.success('Contrato assinado com sucesso!');
      onSigned && onSigned(r.data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao assinar contrato');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-primary" /> Assinar contrato</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          {[1, 2].map((s) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? 'bg-primary' : 'bg-muted'}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/40 p-3 text-sm">
              <div className="font-medium">{sale?.trip_nome}</div>
              <div className="text-muted-foreground">{sale?.destino}</div>
              <div className="mt-1 font-semibold tabular-nums">{formatBRL(sale?.valor_total)}</div>
            </div>
            <ScrollArea className="h-48 rounded-lg border p-4" data-testid="contract-terms-scroll">
              <h4 className="font-semibold text-sm mb-2">Termos e Condições</h4>
              <ol className="list-decimal pl-4 space-y-2 text-sm text-muted-foreground">
                {terms.map((t, i) => <li key={i}>{t}</li>)}
              </ol>
              {cancelPolicy.length > 0 && (
                <div className="mt-4 pt-3 border-t" data-testid="contract-cancellation-section">
                  <h4 className="font-semibold text-sm mb-2">Cláusula de Rescisão e Cancelamento</h4>
                  {cancelIntro && <p className="text-sm text-muted-foreground mb-2">{cancelIntro}</p>}
                  <ul className="space-y-1.5 text-sm">
                    {cancelPolicy.map((t, i) => {
                      const pct = Number(t.reembolso || 0);
                      const valor = Number(sale?.valor_total || 0) * pct / 100;
                      return (
                        <li key={i} className="flex justify-between gap-3 border-b border-dashed border-border/60 pb-1" data-testid={`contract-tier-${i}`}>
                          <span className="text-muted-foreground">{t.label}</span>
                          <span className="font-medium tabular-nums whitespace-nowrap">{pct}% · {formatBRL(valor)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </ScrollArea>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="contract-accept-checkbox" className="mt-0.5" />
              <span className="text-sm">Li e concordo com os termos e condições do contrato.</span>
            </label>
            <Button className="w-full" disabled={!accepted} onClick={() => setStep(2)} data-testid="contract-next-button">Continuar para assinatura</Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Assine no quadro abaixo usando o mouse ou o dedo.</p>
            <div className="rounded-lg border bg-white">
              <SignatureCanvas ref={sigRef} penColor="#0b2a3a" canvasProps={{ className: 'sig-canvas w-full', height: 180 }} />
            </div>
            <div className="flex items-center justify-between">
              <Button type="button" variant="ghost" size="sm" onClick={clearSig} data-testid="contract-clear-signature-button"><Eraser className="h-4 w-4 mr-2" /> Limpar</Button>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Registro com data e hora</span>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" className="flex-1" onClick={() => setStep(1)}>Voltar</Button>
              <Button className="flex-1" onClick={submit} disabled={submitting} data-testid="contract-submit-signature-button">
                {submitting ? 'Enviando...' : <><Check className="h-4 w-4 mr-2" /> Confirmar assinatura</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
